import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  deleteField
} from 'firebase/firestore'
import { db } from '../../lib/firebase'
import {
  UserPOI,
  CreateUserPOIData,
  UpdateUserPOIData
} from '../../types/userPois'
import { deleteUserPOIPhoto } from './storage'

const USER_POIS_COLLECTION = 'user_pois'

/**
 * Create a new user POI
 */
export async function createUserPOI(
  poiData: CreateUserPOIData,
  authorId: string,
  authorName: string,
  authorPhoto?: string
): Promise<string> {
  const userPoi: any = {
    ...poiData,
    authorId,
    authorName,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    confirmations: {},
    outdatedVotes: {},
    resolved: false
  }

  // Only add optional fields if they have values
  if (authorPhoto) {
    userPoi.authorPhoto = authorPhoto
  }

  if (poiData.expiresAt) {
    userPoi.expiresAt = Timestamp.fromDate(poiData.expiresAt)
  }

  if (poiData.photos && poiData.photos.length > 0) {
    userPoi.photos = poiData.photos
  }

  const docRef = await addDoc(collection(db, USER_POIS_COLLECTION), userPoi)
  return docRef.id
}

/**
 * Get all user POIs for a route
 */
export async function getUserPOIsForRoute(routeId: string): Promise<UserPOI[]> {
  const q = query(
    collection(db, USER_POIS_COLLECTION),
    where('routeId', '==', routeId),
    orderBy('createdAt', 'desc')
  )

  const snapshot = await getDocs(q)
  
  // Filter out expired POIs
  const now = Timestamp.now()
  
  return snapshot.docs
    .map(doc => {
      const data = doc.data()
      
      // Handle backward compatibility - convert arrays or simple objects to new format
      if (Array.isArray(data.confirmations)) {
        const confirmationsObj: { [userId: string]: any } = {}
        data.confirmations.forEach((userId: string) => {
          confirmationsObj[userId] = {
            timestamp: data.createdAt || Timestamp.now()
          }
        })
        data.confirmations = confirmationsObj
      } else if (data.confirmations) {
        // Convert simple timestamp values to objects
        Object.keys(data.confirmations).forEach(userId => {
          if (!(data.confirmations[userId].timestamp)) {
            data.confirmations[userId] = {
              timestamp: data.confirmations[userId]
            }
          }
        })
      }
      
      if (Array.isArray(data.outdatedVotes)) {
        const outdatedObj: { [userId: string]: any } = {}
        data.outdatedVotes.forEach((userId: string) => {
          outdatedObj[userId] = {
            timestamp: data.createdAt || Timestamp.now()
          }
        })
        data.outdatedVotes = outdatedObj
      } else if (data.outdatedVotes) {
        // Convert simple timestamp values to objects
        Object.keys(data.outdatedVotes).forEach(userId => {
          if (!(data.outdatedVotes[userId].timestamp)) {
            data.outdatedVotes[userId] = {
              timestamp: data.outdatedVotes[userId]
            }
          }
        })
      }
      
      // Ensure objects exist even if empty
      data.confirmations = data.confirmations || {}
      data.outdatedVotes = data.outdatedVotes || {}
      
      return {
        id: doc.id,
        ...data
      } as UserPOI
    })
    .filter(poi => {
      // Keep if no expiry or not expired yet
      return !poi.expiresAt || poi.expiresAt.toMillis() > now.toMillis()
    })
}

/**
 * Update a user POI
 */
export async function updateUserPOI(
  poiId: string,
  updates: UpdateUserPOIData
): Promise<void> {
  const docRef = doc(db, USER_POIS_COLLECTION, poiId)
  
  const updateData: any = {
    updatedAt: serverTimestamp()
  }

  // Only add fields that have values
  if (updates.name !== undefined) {
    updateData.name = updates.name
  }

  if (updates.description !== undefined) {
    updateData.description = updates.description
  }

  if (updates.coordinates) {
    updateData.coordinates = updates.coordinates
  }

  if (updates.expiresAt) {
    updateData.expiresAt = Timestamp.fromDate(updates.expiresAt)
  }

  if (updates.resolved !== undefined) {
    updateData.resolved = updates.resolved
    if (updates.resolved) {
      updateData.resolvedAt = serverTimestamp()
    }
  }

  if (updates.photos !== undefined) {
    updateData.photos = updates.photos
  }

  await updateDoc(docRef, updateData)
}

/**
 * Delete a user POI
 */
export async function deleteUserPOI(poiId: string): Promise<void> {
  // Get the POI to check for photos
  const poiDoc = await getDoc(doc(db, USER_POIS_COLLECTION, poiId))
  
  if (poiDoc.exists()) {
    const poiData = poiDoc.data()
    
    // Delete all photos from storage
    if (poiData.photos && Array.isArray(poiData.photos)) {
      for (const photoUrl of poiData.photos) {
        try {
          await deleteUserPOIPhoto(photoUrl)
        } catch (error) {
          console.error('Failed to delete photo during POI deletion:', error)
        }
      }
    }
  }
  
  // Delete the document
  await deleteDoc(doc(db, USER_POIS_COLLECTION, poiId))
}

/**
 * Confirm a user POI as accurate
 */
export async function confirmUserPOI(
  poiId: string, 
  userId: string,
  userName?: string,
  userPhoto?: string
): Promise<void> {
  const docRef = doc(db, USER_POIS_COLLECTION, poiId)
  const voteData: any = {
    timestamp: serverTimestamp()
  }
  
  if (userName) voteData.userName = userName
  if (userPhoto) voteData.userPhoto = userPhoto
  
  await updateDoc(docRef, {
    [`confirmations.${userId}`]: voteData,
    [`outdatedVotes.${userId}`]: deleteField() // Remove from outdated votes if they previously marked as outdated
  })
}

/**
 * Mark a user POI as outdated
 */
export async function markUserPOIOutdated(
  poiId: string, 
  userId: string,
  userName?: string,
  userPhoto?: string
): Promise<void> {
  const docRef = doc(db, USER_POIS_COLLECTION, poiId)
  const voteData: any = {
    timestamp: serverTimestamp()
  }
  
  if (userName) voteData.userName = userName
  if (userPhoto) voteData.userPhoto = userPhoto
  
  await updateDoc(docRef, {
    [`outdatedVotes.${userId}`]: voteData,
    [`confirmations.${userId}`]: deleteField() // Remove from confirmations if they previously confirmed
  })
}

/**
 * Remove confirmation/outdated vote from a user POI
 */
export async function removeUserPOIVote(poiId: string, userId: string): Promise<void> {
  const docRef = doc(db, USER_POIS_COLLECTION, poiId)
  await updateDoc(docRef, {
    [`confirmations.${userId}`]: deleteField(),
    [`outdatedVotes.${userId}`]: deleteField()
  })
}